import { Loader2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'src/components/ui/sheet';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import type { VerticalProfile, VerticalProfileWritePayload } from '../types/vertical-profile.types';
import { VerticalProfileForm } from './vertical-profile-form';

export type EntitySheetMode = 'create' | 'edit';

interface VerticalProfileSheetProps {
  open: boolean;
  mode: EntitySheetMode;
  profile: VerticalProfile | null;
  isSaving?: boolean;
  submitError?: string | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: VerticalProfileWritePayload) => void | Promise<void>;
}

export function VerticalProfileSheet({
  open,
  mode,
  profile,
  isSaving = false,
  submitError = null,
  onOpenChange,
  onSubmit,
}: VerticalProfileSheetProps) {
  const isCreate = mode === 'create';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto bg-white p-0 sm:max-w-3xl">
        <div className="border-b border-gray-400 px-6 py-5">
          <SheetHeader className="space-y-1 text-left">
            <SheetTitle className="text-xl font-medium text-black">
              {isCreate ? 'New vertical profile' : profile?.payload.name ?? 'Vertical profile'}
            </SheetTitle>
            <SheetDescription className="text-sm text-gray-500">
              Edits create a new append-only version via the vertical profiles API.
            </SheetDescription>
          </SheetHeader>
          {!isCreate && profile ? (
            <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-xs font-medium text-gray-500">Profile ID</dt>
                <dd className="mt-0.5 text-black">{profile.profile_id}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Version</dt>
                <dd className="mt-0.5 text-black">{profile.version ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Updated</dt>
                <dd className="mt-0.5 text-black">{formatDisplayDateTime(profile.updated_at)}</dd>
              </div>
            </dl>
          ) : null}
        </div>

        <div className="flex-1 px-6 py-5">
          <VerticalProfileForm
            key={profile?.id ?? 'create'}
            initialProfile={isCreate ? null : profile}
            isCreate={isCreate}
            isSaving={isSaving}
            submitError={submitError}
            onSubmit={onSubmit}
            onCancel={() => onOpenChange(false)}
          />
        </div>

        {isSaving ? (
          <div className="sticky bottom-0 flex items-center gap-2 border-t border-gray-400 bg-black px-6 py-3 text-sm text-white" role="status">
            <Loader2 className="size-4 animate-spin" />
            Saving…
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
